import { Box, Button, Dialog, Typography } from '@mui/material';
import { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import { DidoxPreviewModalRef } from './types';
import { useOpen } from '@/app/hooks';
import { useLazyGetDocHtmlQuery, useLazyGetDocPdfQuery } from '@/entities/didox/api';
import ModalTemplate from '@/shared/modal/modal-template';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@/shared/contexts/NotificationContext';
import { getFetchEror } from '@/app/helpers';
import { UILoader } from '@/shared/ui/ui-loader';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack5';
interface Props {
  showPdf?: boolean;
  showHtml?: boolean;
}

export const DidoxPreviewModal = forwardRef<DidoxPreviewModalRef, Props>(
  ({ showPdf = true, showHtml = true }, ref) => {
    const { t } = useTranslation();
    const { showNotification } = useNotification();
    const { open, setOpen } = useOpen();
    const [docName, setDocName] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [pdfPages, setPdfPages] = useState<number[]>([]);
    const [scale, setScale] = useState(0.5);
    const containerRef = useRef<HTMLDivElement>(null);

    const [getDocHtml, { error }] = useLazyGetDocHtmlQuery();
    const [getPdf, { error: pdfError, data: pdf }] = useLazyGetDocPdfQuery();
    const [pdfFile, setPdfFile] = useState<File | string | null>(null);

    const htmlRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(
      ref,
      () => ({
        open: onOpen,
      }),
      []
    );

    // Функция для расчета оптимального масштаба
    const calculateScale = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const defaultPdfWidth = 595;
        const margin = 100;
        const newScale = (containerWidth - margin) / defaultPdfWidth;
        setScale(Math.min(Math.max(newScale, 0.5), 2));
      }
    };

    useEffect(() => {
      const handleResize = () => {
        calculateScale();
      };

      window.addEventListener('resize', handleResize);
      calculateScale();

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, [open]);

    const onOpen: DidoxPreviewModalRef['open'] = async ({ file, id, name }) => {
      try {
        setLoading(true);
        setDocName(name);
        setOpen(true);
        setPdfFile(null);
        if (id) {
          const promises = [getDocHtml];
          if (!showHtml) {
            promises.pop();
          }
          if (showPdf) {
            promises.push(getPdf);
          }
          const [html] = await Promise.all(promises.map((fn) => fn(id).unwrap()));
          if (showHtml && htmlRef.current) {
            htmlRef.current.innerHTML = html;
          }
        } else if (file) {
          if (typeof file === 'string') {
            const url = new URL(file);
            const res = await fetch('/api' + url.pathname).then((res) => res.blob());
            // const docFile = new File([res], name, { type: 'text/html' });
            setPdfFile(res as File);
          } else setPdfFile(file);
        }
      } catch (err) {
        showNotification(getFetchEror(err), 'error');
      } finally {
        setLoading(false);
      }
    };

    const onClose = () => {
      setOpen(false);
      setTimeout(() => {
        setDocName('');
        setLoading(false);
        setPdfPages([]);
        setPdfFile(null);
      }, 300);
    };

    const onLoadSuccess = ({ numPages }: { numPages: number }) => {
      setPdfPages([...Array(numPages)]);
      calculateScale();
    };

    const onDownload = () => {
      const file = pdfFile || pdf;
      if (file) {
        const url = URL.createObjectURL(file as Blob);
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.click();
      }
    };

    return (
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            maxWidth: 'calc(100% - 32px)',
            width: '100%',
            maxHeight: 'calc(100% - 32px)',
            height: '100%',
            margin: '0',
          },
        }}
      >
        <ModalTemplate
          title={docName}
          noBtns
          onClose={onClose}
          titleRightContent={
            <Button onClick={onDownload} disabled={loading}>
              {t('download')}
            </Button>
          }
        >
          {loading && showHtml && !showPdf && <UILoader />}
          {showPdf && (error || pdfError) && <Typography>{t('errors:get_doc_error')}</Typography>}
          <Box component='div' ref={htmlRef} />
          {showPdf && (pdf || pdfFile) && (
            <Box
              ref={containerRef}
              sx={{
                width: '100%',
                height: '100%',
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '20px',
                '& .pdf__canvas': {
                  maxWidth: '100%',
                  height: 'auto !important',
                },
              }}
            >
              <Document
                file={pdfFile || pdf || ''}
                onLoadSuccess={onLoadSuccess}
                loading={<UILoader />}
              >
                {pdfPages.map((_i, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: '100%',
                      marginBottom: '20px',
                      '@media print': {
                        pageBreakInside: 'avoid',
                      },
                      '& canvas': {
                        width: '100% !important',
                        height: 'auto !important',
                      },
                    }}
                  >
                    <Page
                      className='pdf__canvas'
                      pageNumber={index + 1}
                      scale={scale}
                      renderTextLayer={false}
                    />
                  </Box>
                ))}
              </Document>
            </Box>
          )}
        </ModalTemplate>
      </Dialog>
    );
  }
);
